import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/local-auth";
import { Loader2, LogIn, UserPlus, UserRoundCheck } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

// Single "Members area" entry point, intentionally kept OUT of the main
// navigation. It adapts to the auth state:
// - not signed in: starts the self-hosted Better Auth sign-in flow;
// - every signed-in account: opens the role-aware private area (/crm).
//
// Security note: this only drives UI routing. Every protected route and every
// backend function enforces its own authorization server-side.
export default function ReservedAreaButton({
  className,
  fullWidth = false,
  compact = false,
  showRegistration = false,
  onNavigate,
}: {
  className?: string;
  fullWidth?: boolean;
  compact?: boolean;
  showRegistration?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation("common");
  const { user, signin, isAuthenticated, isLoading } = useAuth();
  const returnTo =
    typeof window === "undefined"
      ? "/"
      : `${window.location.pathname}${window.location.search}`;
  const registrationPath = `/login?mode=signup&returnTo=${encodeURIComponent(returnTo)}`;

  const handleClick = useCallback(async () => {
    if (!isAuthenticated) {
      try {
        await signin("/crm");
      } catch (err) {
        console.error("Sign-in error:", err);
      }
      return;
    }

    onNavigate?.();
    window.location.assign("/crm");
  }, [isAuthenticated, onNavigate, signin]);

  const accountLabel = isAuthenticated
    ? t("cta.client_area")
    : t("cta.sign_in");
  const signedInTitle = isAuthenticated
    ? t("cta.signed_in_as", {
        name: user?.name || user?.email || t("cta.client_area"),
      })
    : t("cta.reserved_area_signin");

  return (
    <div
      className={cn(
        "flex items-center gap-2",
        fullWidth && "w-full flex-col",
        className,
      )}
    >
      <Button
        onClick={handleClick}
        disabled={isLoading}
        variant={isAuthenticated ? "secondary" : "ghost"}
        size={compact ? "icon" : "default"}
        className={cn(fullWidth && "w-full", compact && "2xl:w-auto 2xl:px-4")}
        aria-label={accountLabel}
        title={signedInTitle}
      >
        {isLoading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isAuthenticated ? (
          <span className="relative">
            <UserRoundCheck className="size-5" />
            <span
              className="absolute -right-1 -top-1 size-2.5 rounded-full border-2 border-background bg-emerald-500"
              aria-hidden="true"
            />
          </span>
        ) : (
          <LogIn className="size-5" />
        )}
        <span className={cn(compact && "hidden 2xl:inline")}>
          {accountLabel}
        </span>
      </Button>
      {!isAuthenticated && !isLoading && showRegistration && (
        <Button asChild variant="outline" className={cn(fullWidth && "w-full")}>
          <Link to={registrationPath} onClick={onNavigate}>
            <UserPlus className="size-4" />
            {t("cta.register")}
          </Link>
        </Button>
      )}
    </div>
  );
}
